import React, { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { Body } from './components/Body';
import { Footer } from './components/Footer';
import { DashboardSkeletonLoader } from './components/DashboardSkeletonLoader';
import type { PageType, DeviceType, DashboardConfig } from './types';
import { useAuditStore } from './stores/auditStore';

/**
 * Check if CSS is loaded by verifying stylesheet is accessible
 */
function isCssLoaded(): boolean {
  try {
    // Check if CSS link element exists and has a sheet
    const cssLink = (window as any).wya11yCssLink;
    if (!cssLink) {
      // If no CSS URL was provided, assume styles are inline or not needed
      return true;
    }
    
    // Check if stylesheet is loaded
    if (cssLink.sheet) {
      // Try to access cssRules to verify it's fully loaded
      try {
        // For same-origin stylesheets, we can check rules
        if (cssLink.sheet.cssRules && cssLink.sheet.cssRules.length > 0) {
          return true;
        }
        // If sheet exists but no rules, might still be loading or empty
        // But sheet existence is a good indicator
        return true;
      } catch (e) {
        // Cross-origin stylesheets throw errors, but sheet existence means it's loaded
        return true;
      }
    }
    
    return false;
  } catch (error) {
    // If we can't check, assume it's loaded to prevent blocking
    return true;
  }
}

const Dashboard: React.FC<DashboardConfig> = ({ 
  onClose, 
  currentUrl, 
  selectedDevice: propSelectedDevice = 'desktop',
  onDeviceChange: propOnDeviceChange
}) => {
  const [currentPage, setCurrentPage] = useState<PageType>('overview');
  const [cssLoaded, setCssLoaded] = useState(false);
  const { selectedDevice: globalDevice, setSelectedDevice: setGlobalDevice, auditResult } = useAuditStore();
  
  // Check CSS loading status
  useEffect(() => {
    // Check immediately
    if (isCssLoaded()) {
      setCssLoaded(true);
      return;
    }
    
    // Poll for CSS load with timeout
    let checkInterval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;
    let attempts = 0;
    const maxAttempts = 100; // 5 seconds at 50ms intervals
    
    const checkCss = () => {
      attempts++;
      if (isCssLoaded() || attempts >= maxAttempts) {
        clearInterval(checkInterval);
        clearTimeout(timeout);
        setCssLoaded(true);
      }
    };
    
    checkInterval = setInterval(checkCss, 50);
    
    // Fallback timeout - show dashboard anyway after 5 seconds
    timeout = setTimeout(() => {
      clearInterval(checkInterval);
      setCssLoaded(true);
    }, 5000);
    
    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, []);

  // Update local state when prop changes
  useEffect(() => {
    setGlobalDevice(propSelectedDevice);
  }, [propSelectedDevice, setGlobalDevice]);

  // Navigate to overview page when audit result is cleared (re-audit scenario)
  useEffect(() => {
    
    if (auditResult === null && currentPage !== 'overview') {
      setCurrentPage('overview');
    }
  }, [auditResult, currentPage]);

  const handleNavigate = (page: PageType) => {
    setCurrentPage(page);
  };

  const handleDeviceChange = (device: DeviceType) => {
    setGlobalDevice(device);
    
    // Call the prop function if provided (this will update the iframe)
    if (propOnDeviceChange) {
      propOnDeviceChange(device);
    }
  };

  // Expose audit store globally for content script
  useEffect(() => {
    (window as any).useAuditStore = useAuditStore;
    return () => {
      delete (window as any).useAuditStore;
    };
  }, []);

  // Show skeleton loader while CSS is loading
  if (!cssLoaded) {
    return <DashboardSkeletonLoader />;
  }

  return (
    <div className="cy:relative cy:w-full cy:h-screen cy:bg-white cy:border-gray-200 cy:shadow-lg cy:flex cy:flex-col cy:font-sans cy:text-sm cy:leading-relaxed cy:text-gray-800">
      <Header
        onClose={onClose}
        selectedDevice={globalDevice}
        onDeviceChange={handleDeviceChange}
      />
      
      <div className="cy:flex-1 cy:flex cy:flex-col cy:min-h-0">
        <Body
          currentPage={currentPage}
          onNavigate={handleNavigate}
          currentUrl={currentUrl}
        />
      </div>
      
      <Footer />
    </div>
  );
};

export default Dashboard;

